import {defineStore} from 'pinia';
import {globals} from 'hit-online-web-ui/src/globals';
import I18nKey from '../../models/i18n/I18nKey';
import uuid from '@hit/components/src/utils/uuid/uuid';
import {DataService, ActionService, NotificationService} from '../../api';
import {useActionStore} from './action';
import {translate} from '@hit/base/src/plugins/i18n/i18n';

export class HitBannerNotificationItem {
  static WARNING = 'warning';
  static INFO = 'info';
  static SUCCESS = 'success';
  static DANGER = 'danger';

  constructor(id, message, level, details, customButton) {
    this.id = id;
    this.level = level;
    this.count = 1;
    if (message instanceof I18nKey) {
      this.message = message.key;
      this.messageNoItems = message.noItems;
    } else {
      this.message = message;
      this.messageNoItems = null;
    }
    this.details = details;
    this.customButton = customButton;
  }
}

export const useNotificationsStore = defineStore('notifications', {
  state: () => ({
    notificationsMap: new Map(),
    bannersMap: new Map(),
    slowTasksMap: new Map(),

    actionFlag: false, //used to refresh actionList
    gitUuidRegex: /0000-4000-8000-000000000000$/,
    openSlowTask: false,
  }),
  getters: {
    // Getters to obtain list of different notification types
    notifications: (state) => Array.from(state.notificationsMap.values()),
    banners: (state) => Array.from(state.bannersMap.values()),
    slowTasks: (state) => Array.from(state.slowTasksMap.values()),

    runningTaskProgress: (state) => {
      const runningTask = state.slowTasks.find(
        (task) => task.progress >= 0 && task.progress < 100,
      );
      return runningTask ? runningTask?.progress : undefined;
    },

    runningBackgroundTasks: (state) => {
      return state.slowTasks.filter((t) => t.progress >= 0 && t.progress < 100)
        .length;
    },

    unreadNotifications: (state) => {
      return state.notifications.filter((notif) => !notif.read);
    },
    forceShow: (state) => {
      return state.notifications.some((n) => n.show === true);
    },
  },
  actions: {
    /**
     * Inserts the notification into the notification store
     * @param notification
     */
    async insertNotification(notification, fromDatabase = false) {
      // This notification type is used to execute special code on client side
      if (notification.type.toUpperCase() === 'SYSTEM') {
        switch (notification.body) {
          case 'reload':
            this.insertBannerNotification(
              translate('hit-base.banners.page-reload'),
              HitBannerNotificationItem.WARNING,
            );
            setTimeout(async () => {
              NotificationService.deleteSystemNotification(
                notification.notification_id,
              ).then(() => {
                location.reload(true);
              });
            }, 10000);
            break;
          case 'reload-assigned-actions':
            await useActionStore().updateAssignedActions();
            break;
          default:
            throw Error('Unsupported system notification');
        }
      }
      // This notification type is used to handle the actions
      else if (notification.type.toUpperCase() === 'ACTION') {
        this.actionFlag = false;
        try {
          notification.body = JSON.parse(notification.body);
        } catch (error) {
          // If JSON parsing fails, assume the body is a simple string and continue
        }
        this.handleActionNotification(notification);
      } else if (notification.type.toUpperCase() === 'TASK') {
        if (!('progress' in notification)) {
          notification['progress'] = null;
        }
        /*eslint no-empty: "error"*/
        try {
          notification.body = JSON.parse(notification.body);
        } catch {
          // When this blocks throws an error, the body is a simple string. We continue without parsing it
        }
        this.slowTasksMap.set(notification.notification_id, notification);
        this.openSlowTask = false;
        if (
          !fromDatabase &&
          (notification.progress === 100 || notification.progress === -1)
        ) {
          this.openSlowTask = true;
          this.serviceWorkerNotification(notification);
        }
      } else {
        this.actionFlag = false;
        /*eslint no-empty: "error"*/
        try {
          notification.body = JSON.parse(notification.body);
        } catch {
          // When this blocks throws an error, the body is a simple string. We continue without parsing it
        }
        if (!('read' in notification)) {
          notification['read'] = false;
        }
        this.actionFlag = true;
        this.notificationsMap.set(notification.notification_id, notification);
        if (notification.ttl > 0) {
          setTimeout(
            () => this.deleteNotification(notification.notification_id),
            notification.ttl * 1000,
          );
        }

        if (!fromDatabase) {
          this.serviceWorkerNotification(notification);
        }
      }
    },

    async serviceWorkerNotification(notification) {
      if ('serviceWorker' in navigator && 'Notification' in window) {
        try {
          const registration = await navigator.serviceWorker.getRegistration();

          if (!registration) {
            console.warn(
              'Service Worker not registered, notification will not be shown',
            );
            return;
          }

          if (Notification.permission !== 'granted') {
            console.warn(
              'Notification permission not granted, notification will not be shown',
            );
            return;
          }

          const tag = `notification-${notification.notification_id}`;
          const existingNotifications = await registration.getNotifications({
            tag,
          });

          if (existingNotifications.length > 0) {
            return;
          }

          if (registration.active) {
            const cleanHtml = (text) => {
              if (typeof text !== 'string') return text;
              return text.replace(/<[^>]*>/g, '');
            };

            let title;
            if (notification.title) {
              title = cleanHtml(translate(notification.title));
            } else {
              title = translate('hit-app.common.hit-online-apps');
            }

            let body;

            if (notification.body && typeof notification.body === 'object') {
              if (notification.body.message && notification.body.payload) {
                body = cleanHtml(
                  translate(
                    notification.body.message,
                    notification.body.payload,
                  ),
                );
              } else if (notification.body.key && notification.body.params) {
                body = cleanHtml(
                  translate(notification.body.key, notification.body.params),
                );
              } else if (
                notification.body.message &&
                notification.body.params
              ) {
                body = cleanHtml(
                  translate(
                    notification.body.message,
                    notification.body.params,
                  ),
                );
              } else if (notification.body.key) {
                body = cleanHtml(
                  translate(notification.body.key, notification.body),
                );
              } else if (notification.body.message) {
                body = cleanHtml(translate(notification.body.message));
              } else {
                const possibleKeys = ['key', 'message', 'text', 'body'];
                let foundKey = null;
                let params = {};

                for (const key of possibleKeys) {
                  if (notification.body[key]) {
                    foundKey = notification.body[key];
                    params = {...notification.body};
                    delete params[key];
                    break;
                  }
                }

                if (foundKey) {
                  body = cleanHtml(translate(foundKey, params));
                } else {
                  body = cleanHtml(
                    translate(
                      'hit-app.common.new-notification',
                      notification.body,
                    ),
                  );
                }
              }
            } else if (typeof notification.body === 'string') {
              body = cleanHtml(translate(notification.body));
            } else if (notification.message) {
              body = cleanHtml(translate(notification.message));
            } else {
              body = translate('hit-app.common.new-notification');
            }

            registration.active.postMessage({
              type: 'SHOW_NOTIFICATION',
              title: title,
              body: body,
              icon: '/favicon.svg',
              tag: tag,
              requireInteraction: false,
              url: process.env.VUE_APP_BASE_URL,
              notificationId: notification.notification_id,
              clickEvent: notification,
              actions: [
                {
                  action: 'open',
                  title: 'Ouvrir',
                  icon: '/favicon.svg',
                },
                {
                  action: 'close',
                  title: 'Fermer',
                  icon: '/favicon.svg',
                },
              ],
            });
          }
        } catch (error) {
          console.error(
            'Error sending notification via service worker:',
            error,
          );
        }
      }
    },

    async clearOldNotifications() {
      if ('serviceWorker' in navigator && 'Notification' in window) {
        try {
          const registration = await navigator.serviceWorker.getRegistration();

          if (registration) {
            const notifications = await registration.getNotifications();
            notifications.forEach((notification) => {
              notification.close();
            });
          }
        } catch (error) {
          console.error('Error clearing old notifications:', error);
        }
      }
    },

    integerToUuid(seed) {
      if (seed < 0 || seed > 0xffffffff) {
        throw new Error('Seed must be a 32-bit integer');
      }
      let seedHex = seed.toString(16).padStart(8, '0');
      return `${seedHex.substring(0, 4)}${seedHex.substring(
        4,
      )}-0000-4000-8000-000000000000`;
    },

    uuidToInteger(uuidStr) {
      let seedHex = uuidStr.substring(0, 8);
      return parseInt(seedHex, 16);
    },

    async handleActionNotification(notification) {
      const userResponse = await DataService.read('staff', {
        attributes: 'id,user_id',
        filters: {user_id: 'eq.' + notification.body.user_info.user_id},
      });

      if (
        !userResponse ||
        !userResponse.data ||
        userResponse.data.length === 0
      ) {
        console.error('No staff ID found for user.');
        return;
      }

      const userStaffId = userResponse.data[0].id;
      const userId = userResponse.data[0].user_id;
      const action_id = this.integerToUuid(
        notification.body.issue_attributes.id,
      );

      // Check if action exists
      const actionResponse = await DataService.read('action', {
        attributes: 'id',
        filters: {id: 'eq.' + action_id},
      });

      let status = 'OPEN';
      let assigneeStaffId = '';

      if (notification.body.assignee_info !== null) {
        const assigneeResponse = await DataService.read('staff', {
          attributes: 'id',
          filters: {
            user_id: 'eq.' + notification.body.assignee_info.assignee_id,
          },
        });

        if (
          assigneeResponse &&
          assigneeResponse.data &&
          assigneeResponse.data.length > 0
        ) {
          status = 'ASSIGNED';
          assigneeStaffId = assigneeResponse.data[0].id;
        }
      }

      if (
        actionResponse &&
        actionResponse.data &&
        actionResponse.data.length > 0
      ) {
        await DataService.update(
          'action',
          {id: 'eq.' + action_id},
          {
            status: status,
            due_date: notification.body.issue_attributes.due_date,
            designation: notification.body.issue_attributes.title,
            description: notification.body.issue_attributes.description,
            modification_date: new Date(),
            modification_user_id: this.uuid,
            assignee_id: assigneeStaffId || null,
          },
        );
      } else {
        await DataService.create('action', {
          id: action_id,
          no: notification.body.issue_attributes.number,
          author_id: userStaffId,
          assignee_id: assigneeStaffId || null,
          url: 'http://gitlab.ficos.com/hit-online/',
          creation_user_id: notification.body.user_info.user_id,
          due_date: notification.body.issue_attributes.due_date,
          designation: notification.body.issue_attributes.title,
          description: notification.body.issue_attributes.description,
          status: status,
        });
      }

      if (assigneeStaffId.length > 0 && status === 'ASSIGNED') {
        await DataService.create('action_history', {
          id: crypto.randomUUID(),
          action_id: action_id,
          operation: 'ASSIGN',
          assignee_id: assigneeStaffId,
          creation_user_id: userId,
        });
      }
      this.actionFlag = true;
      // this.notificationsMap.set(notification.notification_id, notification);   No Need to show this notification, if assigned will show with above code
    },

    async updateGitLabIssue(
      newAssigneeId,
      newTitle,
      newDescription,
      newDueDate,
      newStatus,
      card,
    ) {
      if (card.url === 'http://gitlab.ficos.com/hit-online/') {
        let newAssigneeUser = null;
        if (newAssigneeId !== '') {
          const userResponse = await DataService.read('staff', {
            attributes: 'user_id',
            filters: {id: 'eq.' + newAssigneeId},
          });
          newAssigneeUser = userResponse.data[0].user_id;
        }

        const issueData = {
          newAssigneeUser,
          newTitle,
          newDescription,
          newDueDate,
          newStatus,
          card,
        };
        ActionService.post('gitlab/issues', {
          values: issueData,
        }).catch((_error) => {
          this.showPrintDialog = false;
        });
      }
    },

    setNotificationAsRead(notificationId) {
      const notificationMap = this.notificationsMap.has(notificationId)
        ? this.notificationsMap
        : this.slowTasksMap;
      const notification = notificationMap.get(notificationId);
      if (!notification) return;
      notification.read = true;
      notification.show = false;
      notificationMap.set(notificationId, notification);
      DataService.update(
        'notification',
        {
          notification_id: `eq.${notificationId}`,
          user_id: `eq.${globals.$keycloak.idTokenParsed.sub}`,
        },
        {read: true, show: false},
      );
    },

    /**
     * Updates all the read flags to true
     */
    setAllNotificationsAsRead() {
      Array.from(this.notificationsMap.values()).forEach((notification) => {
        const notificationCopy = {...notification};
        notificationCopy.read = true;
        notificationCopy.show = false;
        this.notificationsMap.set(
          notificationCopy.notification_id,
          notificationCopy,
        );
      });
      DataService.update(
        'notification',
        {user_id: `eq.${globals.$keycloak.idTokenParsed.sub}`},
        {
          read: true,
          show: false,
        },
      ).then((_r) => {});
    },

    /**
     * Deletes the notification locally and removes it from the database
     */
    deleteNotification(id, type = 'notification') {
      if (type === 'notification') {
        this.notificationsMap.delete(id);
        ActionService.delete(`helpers/delete-notification/${id}`);
      } else {
        this.slowTasksMap.delete(id);
        DataService.delete('notification', {
          notification_id: `eq.${id}`,
          user_id: `eq.${globals.$keycloak.idTokenParsed.sub}`,
        });
      }
    },

    deleteAllNotifications(type = 'notification') {
      if (type === 'notification') {
        this.notificationsMap.clear();

        // Delete all notifications from the database that are not linked to an other entity
        DataService.delete('notification', {
          user_id: `eq.${globals.$keycloak.idTokenParsed.sub}`,
          type: 'in.(NOTIFICATION,SUCCESS,ERROR)',
          action_history_id: 'is.null',
        });

        // Mark notifications as deleted that are linked to an other entity (keep history)
        DataService.update(
          'notification',
          {
            user_id: `eq.${globals.$keycloak.idTokenParsed.sub}`,
            action_history_id: 'not.is.null',
            type: 'in.(NOTIFICATION,SUCCESS,ERROR)',
          },
          {deleted: true},
        );
      } else {
        this.slowTasksMap.clear();
        DataService.delete('notification', {
          user_id: `eq.${globals.$keycloak.idTokenParsed.sub}`,
          type: 'eq.TASK',
        });
      }
    },

    /**
     * Actions used for the banner notifications that are directly displayed on top of the application
     */
    insertBannerNotification(
      message,
      level = HitBannerNotificationItem.DANGER,
      details = null,
      customButton = null,
    ) {
      const notificationAlreadyPresent = Array.from(
        this.bannersMap.values(),
      ).some((obj) => obj.message === message);
      if (!notificationAlreadyPresent) {
        const id = uuid.generate();
        this.bannersMap.set(
          id,
          new HitBannerNotificationItem(
            id,
            message,
            level,
            details,
            customButton,
          ),
        );
        return id;
      }
      const existingBanner = Array.from(this.bannersMap.values()).find(
        (obj) => obj.message === message,
      );
      existingBanner.count++;
      return existingBanner.id;
    },

    /**
     * Removes a banner notification from the store
     * @param id
     */
    deleteBannerNotification(id) {
      this.bannersMap.delete(id);
    },
  },
});
